import { useState } from 'react';
import { Paragraph, useSnackbar } from 'component-library';
import { useTranslation } from 'react-i18next';
import type { ContainerTemplate, NewContainer } from '@sr/types';
import { ContainerTemplateModal, type ContainerTemplateModalKind } from './ContainerTemplateModal';
import { ContainerTemplateMenu } from './ContainerTemplateMenu';
import { applyTemplateRows } from './templateRows';
import {
  cancelTemplateSelection,
  confirmTemplateSelection,
  requestTemplateSelection,
  templateKey,
} from './templateSelection';
import { useTemplateSelection } from './TemplateSelectionProvider';

export const ContainerTemplateActions = ({
  templates,
  containers,
  onApply,
  onCreate,
  onDelete,
  onUpdate,
  isPending,
  error,
}: {
  templates: ContainerTemplate[];
  containers: NewContainer[];
  onApply: (containers: NewContainer[]) => void;
  onCreate: (name: string, containers: NewContainer[]) => Promise<void>;
  onDelete: (record: ContainerTemplate) => Promise<void>;
  onUpdate: (record: ContainerTemplate, containers: NewContainer[]) => Promise<void>;
  isPending: boolean;
  error: unknown;
}) => {
  const { t } = useTranslation();
  const { selection, setSelection } = useTemplateSelection();
  const [modal, setModal] = useState<ContainerTemplateModalKind | null>(
    selection.pending ? 'replace' : null,
  );
  const [name, setName] = useState('');
  const [deleting, setDeleting] = useState<ContainerTemplate>();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const { showSnackbar } = useSnackbar();
  const selected = templates.find((template) => templateKey(template) === selection.selectedKey);
  const disabled = isPending || busy || !!error;
  const defaultError = t('container-templates.error');
  const payloadErrors: Record<string, string> = {
    'container-templates.version.unsupported': t('container-templates.version.unsupported'),
    'container-templates.payload.invalid': t('container-templates.payload.invalid'),
  };
  const errorMessage =
    error instanceof Error ? (payloadErrors[error.message] ?? defaultError) : defaultError;
  const apply = (record: ContainerTemplate) => {
    setSelection(confirmTemplateSelection(record));
    onApply(applyTemplateRows(record.data.containers));
    setModal(null);
  };
  const run = async (action: () => Promise<void>, deleted = false) => {
    setBusy(true);
    setFailed(false);
    try {
      await action();
      setModal(null);
      showSnackbar({
        severity: 'success',
        description: deleted
          ? t('container-templates.delete.success')
          : t('container-templates.save.success'),
      });
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    if (modal === 'replace' && selection.pending) apply(selection.pending);
    else if (modal === 'create')
      await run(async () => {
        await onCreate(name.trim(), structuredClone(containers));
        setName('');
      });
    else if (modal === 'delete' && deleting)
      await run(async () => {
        await onDelete(deleting);
        if (templateKey(deleting) === selection.selectedKey) {
          setSelection({});
        }
        setDeleting(undefined);
      }, true);
  };

  return (
    <>
      {(!!error || failed) && (
        <div role="alert">
          <Paragraph>{errorMessage}</Paragraph>
        </div>
      )}
      {isPending && <Paragraph>{t('container-templates.loading')}</Paragraph>}
      <ContainerTemplateMenu
        templates={templates}
        selected={selected}
        selectedKey={selection.selectedKey}
        disabled={disabled}
        hasContainers={!!containers.length}
        onDelete={(id) => {
          if (disabled) return;
          setDeleting(templates.find((record) => templateKey(record) === id));
          setModal('delete');
        }}
        onSelect={(key) => {
          if (disabled) return;
          if (key === undefined) {
            setSelection({});

            return;
          }
          const record = templates.find((template) => templateKey(template) === key);

          if (!record) return;
          const next = requestTemplateSelection(selection, containers, record);

          if (next.pending) {
            setSelection(next);
            setModal('replace');
          } else apply(record);
        }}
        onCreate={() => {
          setFailed(false);
          setModal('create');
        }}
        onUpdate={(record) =>
          run(async () => {
            await onUpdate(record, structuredClone(containers));
            setSelection({ ...selection, baseline: structuredClone(containers) });
          })
        }
      />
      <ContainerTemplateModal
        modal={modal}
        name={name}
        busy={busy}
        disabled={disabled}
        hasContainers={!!containers.length}
        failed={failed}
        onNameChange={setName}
        onCancel={() => {
          setSelection(cancelTemplateSelection(selection));
          setModal(null);
        }}
        onConfirm={confirm}
      />
    </>
  );
};
